import { memo } from 'react';
import {
  Breadcrumbs as MUIBreadcrumbs,
  BreadcrumbsProps as MUIBreadcrumbsProps,
} from '@mui/material';

import classNames from 'classnames';
import { S4, S6, AB3 } from '../../theme';
import { CaretRightIcon } from '../../icons';
import Spinner from '../../animations/Spinner';
import TypographyWithTooltip from '../TypographyWithTooltip';
import { mergeSX } from '../../services/helpers';

interface IBuildSXParams {
  isLoading: boolean;
  truncateLast: boolean;
}

const buildSX = ({ isLoading, truncateLast }: IBuildSXParams) => ({
  width: '100%',
  '& .MuiBreadcrumbs-ol': {
    justifyContent: isLoading ? 'center' : 'start',
    flexWrap: 'nowrap',
  },
  '& .MuiBreadcrumbs-li': {
    maxWidth: 200,
    '&:first-of-type': {
      minWidth: 'fit-content',
    },
    '&:last-of-type': {
      cursor: 'initial',
      color: S6,
      maxWidth: truncateLast ? 200 : '100%',
    },
  },
  '& .MuiBreadcrumbs-separator': {
    margin: '0 16px',
  },
  '& .clickable .MuiBox-root:hover': {
    cursor: 'pointer',
    color: AB3,
  },
});

export interface IBreadcrumbNode {
  onClick?: () => void;
  title: string;
}

export interface IBreadcrumbsProps extends MUIBreadcrumbsProps {
  isLoading?: boolean;
  nodes: IBreadcrumbNode[];
  truncateLast?: boolean;
}

const SEPARATOR = <CaretRightIcon width={5} height={10} color={S4} />;

const Breadcrumbs = (props: IBreadcrumbsProps) => {
  const {
    sx, isLoading = false, nodes, truncateLast = false, ...restProps
  } = props;
  return (
    <MUIBreadcrumbs
      sx={mergeSX(buildSX({ isLoading, truncateLast }), sx)}
      color={S4}
      separator={SEPARATOR}
      {...restProps}
    >
      {isLoading ? <Spinner /> : nodes.map(({ onClick, title }) => (
        <TypographyWithTooltip
          key={title}
          variant="subtitle1"
          className={classNames({ clickable: !!onClick })}
          onClick={onClick}
          breakWord
        >
          {title}
        </TypographyWithTooltip>
      ))}
    </MUIBreadcrumbs>
  );
};

export { default as BreadcrumbsContainer } from './components/BreadcrumbsContainer';
export default memo(Breadcrumbs);
